import type { StoredArtifactReference } from '../artifactReference';
import type { ProjectItemInfo } from './premiereInfo';

export interface Clip {
  // Starting time of the track item in the sequence (in ms)
  start: number;

  // End time of the track item in the sequence (in ms)
  end: number;

  /**
   * This should ideally be derivable as (end - start). But we still record it since Premiere reports
   * all 3 props. TODO: consider removing this.
   */
  duration?: number;

  /**
   * Start and end times relative to the source clip (in ms), as reported on the track item
   * object by Premiere. These represent the times after Premiere has applied speed
   * and reversal on the clip, and it can also be different depending on whether the track
   * item is created from a regular clip/subclip (then times are relative to the original media
   * duration), or a subclip that has restricted trims (then times are relative to the subclip
   * duration).
   */
  inPointPremiere?: number;
  outPointPremiere?: number;

  /**
   * Our interpretation of inPointPremiere and outPointPremiere (in ms).
   * Our code (in source copy operations and for downloading required segments for rendering
   * segmented highres) depends on inPoint and outPoint being relative to the full source media
   * duration, and without any speed/reversal applied. Currently at the time of export, we can
   * correctly calculate these when speed is applied, but we cannot do so when clip is reversed
   * (because that would require us to get the correct duration of the source media, which is not
   * trivial).
   */
  inPoint: number;
  outPoint: number;

  /**
   * Default is 1. Double speed is 2. Half speed is 0.5. Etc.
   */
  speed?: number;

  /**
   * Whether the clip is supposed to play backwards
   */
  isSpeedReversed?: boolean;


  mimirItemId?: string;
  startOffsetMasterClip?: number;
  mediaPath?: string;
  nodeId?: string;
  key?: string;

  // Carry artifact refs needed for segmented media
  artifact?: StoredArtifactReference;
}


export enum MediaType {
  VIDEO = 'video',
  AUDIO = 'audio',
}


export interface Track {
  id?: number;              // number, given by premiere. Example: 1, 2
  name?: string;            // user-editable. Example: 'Mono', 'Speak'
  mediaType?: MediaType;    // 'audio' or 'video'
  clips: Clip[];
  trackLabel: string;       // synthesized from track type and array order. Example: 'V1', 'A2'
}


export interface SequenceDetails {
  projectType?: 'prproj' | 'fcpx';
  tracks?: Track[];
  sequenceInPointInMs: number;
  sequenceOutPointInMs: number;
}


export interface SequenceExportInfo {
  exportedFilename: string;
  exportedFileExtension: string;
  parentItemIds: string[];
  tracks: Track[];
  /**
   * A map of Mimir items that are specifically part of the portion of a sequence being exported.
   * Keys are Mimir IDs. They contain objects that are keyed by a combination of the clip's
   * file path and a 'node ID' assigned by premiere and values are title of the clip in the bin.
   */
  mimirClipInfos: Record<string, Record<string, string>>;
  errorMessage?: string;
  warnings?: string[];
  sequenceKey: string;    // with project path, more unique
  sequenceName: string;
  sequenceId: string;     // without project path, not reliably unique in multi project env
  sequenceInPointInMs: number;
  sequenceOutPointInMs: number;
  projectPath: string;
}


export interface SequenceInfo {
  exportInfo: SequenceExportInfo;
  itemInfos: ProjectItemInfo[];
}

